import { useState } from "react";

import clsx from "clsx";
import moment from "moment";

import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { type User } from "@internal/user";
import {
  type ITimeGranularityV2,
  type CompareDateRangeV2,
  type DateInterval,
} from "@north-beam/nb-common";

import { PrimaryButton, SecondaryButton } from "../buttons";
import { Calendar } from "../calendar";
import { OptionItem } from "../date-picker/option-item";

import { QUICK_SELECT_OPTIONS } from "./constants";

interface Props {
  value: CompareDateRangeV2 | null;
  minDate?: string;
  maxDate?: string;
  close: () => void;
  onChange: (value: CompareDateRangeV2 | null) => void;

  referenceDateRange: DateInterval;
  timezone: User["timezone"];
  timeGranularity: ITimeGranularityV2;
}

export const CompareDatePickerPanel = ({
  value,
  minDate,
  maxDate,
  close,
  onChange,
  referenceDateRange,
  timezone,
  timeGranularity,
}: Props) => {
  const [dateRange, setDateRange] = useState<DateInterval | null>(() => {
    if (!value) {
      return null;
    }

    if (value.value !== "custom") {
      const val = deriveComparisonDateRange(
        referenceDateRange,
        value,
        timezone,
        timeGranularity,
      );

      return val;
    }

    return { startDate: value.startDate, endDate: value.endDate };
  });

  return (
    <div className="flex">
      <div className="w-32 p-[1px] border-r border-v2-tertiary">
        {QUICK_SELECT_OPTIONS.map((option) => {
          const startDate = option.relativeDate().format();

          return (
            <OptionItem
              key={option.value}
              option={option}
              disabled={minDate ? moment(minDate).isAfter(startDate) : false}
              selected={!dateRange && value?.value === option.value}
              onClick={() => {
                onChange({ value: option.value });
                close();
              }}
            />
          );
        })}
      </div>
      <div
        className={clsx(
          "w-[580px] bg-v2-highlight px-6 py-4 flex flex-col justify-between",
        )}
      >
        <div>
          <Calendar
            minDate={minDate}
            maxDate={maxDate}
            value={dateRange}
            onChange={(val) => setDateRange(val)}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <SecondaryButton size="sm" onClick={() => close()}>
            CANCEL
          </SecondaryButton>
          <PrimaryButton
            size="sm"
            onClick={() => {
              if (dateRange) {
                onChange({ ...dateRange, value: "custom" });
              }
              close();
            }}
          >
            DONE
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
